import {array_shuffle, sleep} from 'lib/utilities';

export default function TiledFadingGallery(container, opts = {}) {
    let options = resolveOptions(opts);
    let elements = findElements();
    let models = createModels(elements.tiles);
    let timeout = null;

    prepareNextBatch();
    queueNext();

    function resolveOptions(options) {
        return {
            time: 4000,
            stagger: 100,
            imageSelector: 'picture',
            tileSelector: '.tile',
            waitForTransitions: true,
            ...options
        }
    }

    function findElements() {
        const elements = {};

        elements.pool = Array.from(container.querySelectorAll(options.imageSelector));
        elements.tiles = Array.from(container.querySelectorAll(options.tileSelector));

        return elements;
    }

    function createModels(tiles) {
        return tiles.map(createModel);
    }

    function createModel(element, index) {
        return {
            element,
            current: element.querySelector(options.imageSelector),
            next: null,
            index
        }
    }

    function destroy() {
        clearTimeout(timeout);

        // Clear instance variables
        options = elements = models = null;
    }

    function getUnusedPool() {
        const slots = getUsedPool();

        return elements.pool.filter(image => slots.indexOf(image) === -1);
    }

    function getUsedPool() {
        return models.map(model => model.current);
    }

    function getRandomisedSlots() {
        // Take a copy of the pool
        const pool = array_shuffle(getUnusedPool());

        // Pop images off the end of the pool for each slot, falling back on the original image
        const slots = models.map((model) => pool.pop() ?? model.current);

        // Shuffle again to ensure that any fallback images are shifted to another position
        return array_shuffle(slots);
    }

    function queueNext() {
        setTimeout(next, options.time);
    }

    function prepareNextImage(model, image) {
        // If the image hasn't changed, leave it
        if (model.current === image) {
            return;
        }

        image.classList.remove('is-current');
        image.classList.add('is-next');

        model.next = image;
        model.element.appendChild(image);
    }

    function prepareNextBatch() {
        const slots = getRandomisedSlots();

        models.forEach((model, index) => prepareNextImage(model, slots[index]));
    }

    function onTransitionComplete(element, f) {
        const onTransitionEnd = () => {
            element.removeEventListener('transitioncancel', onTransitionEnd);
            element.removeEventListener('transitionend', onTransitionEnd);

            f(element);
        };

        // Detect end of transition to then remove the current element
        element.addEventListener('transitionend', onTransitionEnd);
        element.addEventListener('transitioncancel', onTransitionEnd);
    }

    function showNextImage(model, delay) {
        return new Promise(async resolve => {
            await sleep(delay);

            // If the image is the same, skip this one
            if (model.current === model.next) {
                return;
            }

            const next = model.next;
            const current = model.current;

            function complete() {
                model.element.removeChild(current);
                model.current = model.next;
                model.next = null;

                resolve();
            }

            // Update classes
            current.classList.remove('is-current');
            model.next.classList.remove('is-next');
            model.next.classList.add('is-current');

            if (options.waitForTransitions) {
                onTransitionComplete(next, complete);
            } else {
                complete();
            }
        })
    }

    async function next() {
        const promises = models.map((model, index) => showNextImage(model, index * options.stagger));

        // Wait for all transitions to complete
        await Promise.all(promises);

        prepareNextBatch();
        queueNext();
    }

    return {
        destroy,
        next
    }
}